// src/components/PlayArrow.tsx
// Rendu Konva d'un tracé de système (passe, dribble, cut, écran, tir, handoff).
// Partagé entre PlayCreator, PlayDetails et SharedPlay : toutes les coordonnées
// reçues sont déjà en pixels du Stage.
import { Circle, Group, Line, Path } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import {
  type Pt,
  type Quad,
  midpoint,
  quadTangent,
  trimQuad,
  quadPathData,
  arrowHeadPoints,
  perpendicularBar,
  wavyPoints,
} from "../utils/arrowGeometry";

const PLAYER_GAP = 20; // distance entre le centre du joueur et le début/fin du tracé
const STROKE = 3;
const HEAD_LEN = 11;
const HEAD_WIDTH = 11;
const SCREEN_BAR = 20;
const HIT_STROKE = 16; // zone cliquable autour du trait
const SHOT_TARGET_R = 12; // rayon de la mire du tir
const SHOT_CROSS_OVERHANG = 5; // débord de la croix hors du cercle

type Props = {
  type: string;
  from: Pt;
  to: Pt;
  control?: Pt | null;
  fromIsPlayer?: boolean;
  toIsPlayer?: boolean;
  color?: string;
  /** Rend le glyphe du handoff déplaçable ; reçoit le déplacement depuis le dernier rendu. */
  onHandoffDragStart?: () => void;
  onHandoffDragMove?: (dx: number, dy: number) => void;
};

const CURVED_TYPES = ["cut", "dribble", "screen"];

export const PlayArrow = ({
  type,
  from,
  to,
  control,
  fromIsPlayer = false,
  toIsPlayer = false,
  color = "#000000",
  onHandoffDragStart,
  onHandoffDragMove,
}: Props) => {
  // Passe, tir et handoff sont toujours droits ; les autres suivent le point de contrôle.
  const ctrl = CURVED_TYPES.includes(type) && control ? control : midpoint(from, to);
  const curve: Quad = trimQuad(
    { p0: from, c: ctrl, p2: to },
    fromIsPlayer ? PLAYER_GAP : 0,
    toIsPlayer ? PLAYER_GAP : 0
  );
  const end = curve.p2;
  const endDir = quadTangent(curve, 1);

  if (type === "shot") {
    // trait pointillé qui s'arrête au bord d'une mire (cercle + croix qui dépasse)
    const body = trimQuad(curve, 0, SHOT_TARGET_R);
    const c = SHOT_TARGET_R + SHOT_CROSS_OVERHANG;
    return (
      <Group>
        <Path
          data={quadPathData(body)}
          stroke={color}
          strokeWidth={STROKE}
          lineCap="round"
          dash={[10, 6]}
          hitStrokeWidth={HIT_STROKE}
        />
        <Group x={end.x} y={end.y} listening={false}>
          <Circle radius={SHOT_TARGET_R} stroke={color} strokeWidth={STROKE} />
          <Line points={[-c, 0, c, 0]} stroke={color} strokeWidth={STROKE} lineCap="round" />
          <Line points={[0, -c, 0, c]} stroke={color} strokeWidth={STROKE} lineCap="round" />
        </Group>
      </Group>
    );
  }

  if (type === "handoff") {
    const draggable = !!onHandoffDragMove;
    return (
      <Group
        x={end.x}
        y={end.y}
        draggable={draggable}
        onDragStart={onHandoffDragStart}
        onDragMove={(e: KonvaEventObject<DragEvent>) =>
          onHandoffDragMove?.(e.target.x() - end.x, e.target.y() - end.y)
        }
      >
        <Line points={[-14, 0, 14, 0]} stroke={color} strokeWidth={4} />
        <Line points={[-7, -10, -7, 10]} stroke={color} strokeWidth={4} />
        <Line points={[7, -10, 7, 10]} stroke={color} strokeWidth={4} />
      </Group>
    );
  }

  if (type === "screen") {
    return (
      <Group>
        <Path
          data={quadPathData(curve)}
          stroke={color}
          strokeWidth={STROKE}
          lineCap="round"
          hitStrokeWidth={HIT_STROKE}
        />
        <Line
          points={perpendicularBar(end, endDir, SCREEN_BAR)}
          stroke={color}
          strokeWidth={STROKE}
          lineCap="round"
        />
      </Group>
    );
  }

  // Tracés avec pointe : le trait s'arrête à la base de la pointe pour ne pas dépasser.
  const body = trimQuad(curve, 0, HEAD_LEN);
  const head = (
    <Line points={arrowHeadPoints(end, endDir, HEAD_LEN, HEAD_WIDTH)} closed fill={color} listening={false} />
  );

  if (type === "dribble") {
    return (
      <Group>
        <Line
          points={wavyPoints(body)}
          stroke={color}
          strokeWidth={STROKE}
          lineCap="round"
          lineJoin="round"
          hitStrokeWidth={HIT_STROKE}
        />
        {head}
      </Group>
    );
  }

  // cut (courbe pleine) ou passe (droite pointillée)
  const isPass = type === "arrow" || type === "pass";
  return (
    <Group>
      <Path
        data={quadPathData(body)}
        stroke={color}
        strokeWidth={STROKE}
        lineCap="round"
        dash={isPass ? [10, 6] : undefined}
        hitStrokeWidth={HIT_STROKE}
      />
      {head}
    </Group>
  );
};

export default PlayArrow;
